import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, AlertTriangle, Loader2, ArrowRight, Home } from "lucide-react";
import api from "@/lib/api";
import { BRAND } from "@/lib/brand";

const MAX_POLLS = 8;
const POLL_INTERVAL_MS = 2000;

export default function PaymentReturnPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | expired | failed | error
  const [booking, setBooking] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("tnp_pending_booking") || "null"); } catch { return null; }
  });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let alive = true;
    let attempts = 0;

    const poll = async () => {
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (!alive) return;
        setAttempt(attempts + 1);
        if (res.data.payment_status === "paid") {
          sessionStorage.removeItem("tnp_pending_booking");
          setState("paid");
          return;
        }
        if (res.data.status === "expired") {
          setState("expired");
          return;
        }
        attempts += 1;
        if (attempts >= MAX_POLLS) {
          setState("failed");
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (!alive) return;
        setState("error");
      }
    };
    poll();
    return () => { alive = false; };
  }, [sessionId]);

  return (
    <div className="max-w-2xl mx-auto px-6 py-20" data-testid="payment-return-page">
      <div className="card-elevated p-10 text-center">

        {state === "checking" && (
          <>
            <div className="w-20 h-20 mx-auto rounded-full bg-[var(--brand-teal)]/10 flex items-center justify-center mb-6">
              <Loader2 className="w-10 h-10 text-[var(--brand-teal)] animate-spin" />
            </div>
            <h1 className="heading-display text-3xl">Confirming your payment...</h1>
            <p className="mt-3 text-slate-600">Hang tight — we're checking with Stripe (attempt {attempt + 1} of {MAX_POLLS}).</p>
          </>
        )}

        {state === "paid" && (
          <>
            <div className="w-20 h-20 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-6">
              <CheckCircle2 className="w-10 h-10 text-emerald-600" />
            </div>
            <h1 className="heading-display text-3xl md:text-4xl" data-testid="payment-success-heading">Deposit Confirmed!</h1>
            <p className="mt-3 text-slate-600">
              Your $50 deposit has been received and your slot is locked in.
              {booking?.email && <> A confirmation email will go to <strong>{booking.email}</strong>.</>}
            </p>
            {booking && (
              <div className="mt-6 rounded-xl bg-[var(--brand-cream-soft)] p-5 text-left grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-xs uppercase tracking-wider font-bold text-slate-500">Booking ID</div>
                  <div className="font-mono text-[var(--brand-teal)] text-xs break-all" data-testid="success-booking-id">{booking.id}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider font-bold text-slate-500">Service</div>
                  <div className="font-semibold capitalize">{booking.service_type} Clean • {booking.preferred_date}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider font-bold text-slate-500">Estimate</div>
                  <div className="font-extrabold text-[var(--brand-teal)] text-lg" style={{ fontFamily: "Outfit" }}>${booking.service_estimate.toFixed(2)}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider font-bold text-slate-500">Paid Today</div>
                  <div className="font-extrabold text-emerald-600 text-lg" style={{ fontFamily: "Outfit" }}>$50.00</div>
                </div>
              </div>
            )}
            <p className="mt-5 text-xs text-slate-500 leading-relaxed">
              After your service is completed, we'll text or email you a secure Stripe payment link for the remaining balance of <strong>${(booking?.remaining_balance ?? 0).toFixed(2)}</strong>.
              Any questions? Call us at <a href={BRAND.phoneHref} className="text-[var(--brand-teal)] font-semibold">{BRAND.phone}</a>.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/" className="btn-outline" data-testid="paid-home-btn"><Home className="w-4 h-4" /> Return Home</Link>
              <Link to="/quote" className="btn-primary" data-testid="paid-new-btn">Book Another Service <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </>
        )}

        {(state === "expired" || state === "failed" || state === "error") && (
          <>
            <div className="w-20 h-20 mx-auto rounded-full bg-red-100 flex items-center justify-center mb-6">
              <AlertTriangle className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="heading-display text-3xl">
              {state === "expired" ? "Checkout Session Expired" : state === "failed" ? "We couldn't confirm your payment" : "Something went wrong"}
            </h1>
            <p className="mt-3 text-slate-600">
              {state === "expired"
                ? "Your Stripe checkout session has expired. No charge was made. Please try booking again."
                : "We weren't able to verify your payment after several attempts. If you completed the checkout, please check your email for a Stripe receipt or call us — we'll sort it out instantly."}
            </p>
            <p className="mt-4 text-sm text-slate-500">
              Need help? <a href={BRAND.phoneHref} className="text-[var(--brand-teal)] font-semibold">{BRAND.phone}</a> or <a href={`mailto:${BRAND.email}`} className="text-[var(--brand-teal)] font-semibold">{BRAND.email}</a>.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/" className="btn-outline"><Home className="w-4 h-4" /> Return Home</Link>
              <button onClick={() => navigate("/quote")} className="btn-primary" data-testid="retry-quote-btn">Try Booking Again <ArrowRight className="w-4 h-4" /></button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
